/**
 * Exploitation Race — velocity-first vuln triage.
 *
 * The hero view is "Races You're Losing": for every CVE touching our inventory
 * we predict the exploitation window (red — "exploit lands here") and race it
 * against our realistic remediation window (blue — "we patch here"). When the
 * red bar is shorter than the blue, the gap is the EXPOSURE window — we're
 * exploited before we can patch. Ranked by race_score (blast radius × exposure,
 * credited for the compensating controls we already run).
 *
 * This is the modern shift in vuln management: away from severity-first (CVSS)
 * toward velocity-first (will this be weaponised before I can close it?).
 */
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Timer, RefreshCw, ChevronDown, ChevronRight,
  Zap, ShieldAlert, Shield, Flame, Network,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import RelatedItems from '../components/RelatedItems'
import KpiTile from '../components/KpiTile'
import SeverityBadge from '../components/SeverityBadge'
import PageHeader from '../components/PageHeader'
import ErrorBanner from '../components/ErrorBanner'
import EmptyState from '../components/EmptyState'

interface Factor { factor: string; effect: string; detail: string }
interface Control { control: string; credit: number; source?: string; note?: string }
interface Race {
  cve_id: string
  summary: string | null
  predicted_tte_days: number
  our_mttr_days: number
  exposure_days: number
  losing: boolean
  blast_radius: number
  control_credit: number
  race_score: number
  affected_asset_count: number
  ai_discovered: boolean
  kev: boolean
  factors: Factor[]
  controls_credited?: Control[]
  tiers_hit?: number[]
  dominant_asset_class?: string
  in_toxic_chain?: boolean
  internet_facing?: boolean
  internet_facing_target_days?: number | null
  epss_score?: number
  ransomware?: boolean
  severity?: string | null
  cvss_score?: number
  sources?: string[]
  version_confirmed?: boolean
  matched_components?: string[]
}
interface Summary {
  total_in_inventory: number
  losing_count: number
  winning_count: number
  median_exposure_days: number
  ai_accelerated_count: number
  top_losing: Race[]
}

/**
 * The visual race bar. Two bars on a shared day-axis:
 *   red  = predicted-TTE   ("exploit lands here")
 *   blue = our-MTTR        ("we patch here")
 * When red < blue, the gap between them is shaded as the exposure window.
 */
function RaceBar({ tte, mttr }: { tte: number; mttr: number }) {
  const axisMax = Math.max(mttr, tte, 1) * 1.08
  const pct = (d: number) => `${Math.max(0, Math.min(100, (d / axisMax) * 100))}%`
  const losing = mttr > tte
  const expStart = Math.min(tte, mttr)
  const expWidth = Math.abs(mttr - tte)
  return (
    <div className="w-full select-none">
      {/* exploit bar (red) */}
      <div className="relative h-3 mb-1">
        <div className="absolute inset-0 rounded bg-surface-raised" />
        <div className="absolute inset-y-0 left-0 rounded bg-rose/60" style={{ width: pct(tte) }}
             title={`predicted exploit window ≈ ${tte}d`} />
        {/* exposure shading — overlaid between tte and mttr when losing */}
        {losing && expWidth > 0 && (
          <div className="absolute inset-y-0 rounded bg-rose/15 border-y border-dashed border-rose/40"
               style={{ left: pct(expStart), width: pct(expWidth) }}
               title={`exposure window: ${expWidth.toFixed(1)}d exploited before we patch`} />
        )}
      </div>
      {/* patch bar (blue) */}
      <div className="relative h-3">
        <div className="absolute inset-0 rounded bg-surface-raised" />
        <div className="absolute inset-y-0 left-0 rounded bg-cyan/50" style={{ width: pct(mttr) }}
             title={`our realistic remediation window ≈ ${mttr}d`} />
      </div>
      <div className="flex justify-between mt-1 text-[9px] text-text-muted">
        <span><span className="text-rose-light">▮</span> exploit {tte}d</span>
        {losing
          ? <span className="text-rose-light font-semibold">exposed {expWidth.toFixed(1)}d</span>
          : <span className="text-emerald">we win by {Math.abs(expWidth).toFixed(1)}d</span>}
        <span><span className="text-cyan">▮</span> patch {mttr}d</span>
      </div>
    </div>
  )
}

export default function ExploitationRacePage() {
  const [summary, setSummary] = useState<Summary | null>(null)
  const [races, setRaces] = useState<Race[]>([])
  const [loading, setLoading] = useState(false)
  const [recomputing, setRecomputing] = useState(false)
  const [error, setError] = useState('')
  const [msg, setMsg] = useState('')
  const [showWinning, setShowWinning] = useState(false)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const [s, r] = await Promise.all([
        api.get('/api/exploitation-race/summary'),
        api.get('/api/exploitation-race/all', { params: { limit: 300 } }),
      ])
      setSummary(s.data as Summary)
      setRaces((r.data.items || []) as Race[])
    } catch (e: unknown) {
      setError((e as { message?: string })?.message || 'failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const recompute = async () => {
    setRecomputing(true); setMsg(''); setError('')
    try {
      const r = await api.post('/api/exploitation-race/recompute')
      const d = r.data as { upserted: number; losing: number; total: number }
      setMsg(`Recomputed — ${d.total} raced, ${d.losing} losing.`)
      await load()
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status
      setError(status === 401 ? 'Admin role required to recompute.' : 'Recompute failed.')
    } finally {
      setRecomputing(false)
    }
  }

  const toggle = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const losing = races.filter(r => r.losing).sort((a, b) => b.race_score - a.race_score)
  const winning = races.filter(r => !r.losing).sort((a, b) => b.race_score - a.race_score)
  const empty = races.length === 0

  return (
    <div className="space-y-5 animate-fade-in">
      <PageHeader
        icon={Timer} iconClassName="text-rose-light" title="EXPLOITATION RACE"
        subtitle="velocity-first vuln triage — will it be weaponised before we patch?"
        backTo="/app/zero-day-clock" backLabel="Zero Day Clock"
        actions={<>
          {msg && <span className="text-[10px] text-emerald">{msg}</span>}
          <button type="button" onClick={() => void recompute()} disabled={recomputing} className="btn-ghost">
            {recomputing ? <RefreshCw size={11} className="animate-spin" /> : <Zap size={11} />}
            {recomputing ? 'Computing…' : 'Recompute'}
          </button>
          <button type="button" onClick={() => void load()} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </>}
      />

      <div className="card p-3 text-[12px] text-text-secondary leading-relaxed">
        <span className="text-text-primary font-medium">Velocity-first triage.</span>{' '}
        Which vulnerabilities will be exploited <em>before</em> we can realistically patch —
        racing each CVE's predicted exploitation window (from the Zero Day Clock decay model,
        accelerated by KEV / EPSS / ransomware / AI-discovery) against our remediation window,
        ranked by blast radius and credited for the controls we already run. The CVEs where
        the exploit lands first are the races we're <span className="text-rose-light font-semibold">losing</span>.
        <div className="mt-1.5 text-[10px] text-text-muted">
          Inventory source: service-catalog declared stack + CDM golden assets.
        </div>
      </div>

      <ErrorBanner message={error} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Races we're losing" value={summary?.losing_count ?? 0}
              tone={(summary?.losing_count ?? 0) ? 'text-rose-light' : 'text-text-primary'}
              hint="exploited before we patch" />
        <KpiTile label="Median exposure window" value={summary ? `${summary.median_exposure_days}d` : '—'}
              tone="text-amber-light" hint="days exposed across losing races" />
        <KpiTile label="AI-accelerated" value={summary?.ai_accelerated_count ?? 0}
              tone={(summary?.ai_accelerated_count ?? 0) ? 'text-amber-light' : 'text-text-primary'}
              hint="AI-found → weaponised fast" />
        <KpiTile label="CVEs raced (in inventory)" value={summary?.total_in_inventory ?? 0}
              hint="sparse until SCCM/BMC land" />
      </div>

      {/* ─── The losing list — the centerpiece ──────────────────────────────── */}
      <section className="space-y-2">
        <h2 className="text-sm font-bold text-text-primary flex items-center gap-2">
          <Flame size={15} className="text-rose-light" /> Races You're Losing
          <span className="text-[10px] font-normal text-text-muted">ranked by race score (blast × exposure, controls-credited)</span>
        </h2>

        {empty ? (
          <EmptyState variant="inline" icon={Timer} tone="text-rose-light"
            title="No CVEs in inventory raced yet"
            hint="Run a recompute or wait for the daily pass. Inventory is sparse until SCCM / BMC Helix land — until then the CVE-to-asset linkage is thin." />
        ) : losing.length === 0 ? (
          <EmptyState variant="inline" icon={Shield} tone="text-emerald"
            title="No losing races right now"
            hint="Every raced CVE has a remediation window inside its predicted exploitation window — we patch before the exploit lands. See winning races below." />
        ) : (
          <div className="space-y-2">
            {losing.map(r => <RaceRow key={r.cve_id} r={r} open={expanded.has(r.cve_id)} toggle={toggle} />)}
          </div>
        )}
      </section>

      {/* ─── Winning races (collapsed) ──────────────────────────────────────── */}
      {winning.length > 0 && (
        <section className="space-y-2">
          <button type="button" onClick={() => setShowWinning(v => !v)}
                  className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-text-muted hover:text-text-secondary">
            {showWinning ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <Shield size={12} className="text-emerald/70" />
            Show winning races ({winning.length}) — we patch before the exploit lands
          </button>
          {showWinning && (
            <div className="space-y-2 opacity-70">
              {winning.map(r => <RaceRow key={r.cve_id} r={r} open={expanded.has(r.cve_id)} toggle={toggle} />)}
            </div>
          )}
        </section>
      )}
    </div>
  )
}

function RaceRow({ r, open, toggle }: { r: Race; open: boolean; toggle: (id: string) => void }) {
  const sev = (r.severity || 'medium').toLowerCase()
  const creditPct = Math.round(r.control_credit * 100)
  const topControls = (r.controls_credited || []).slice(0, 3).map(c => c.control)
  const srcs = r.sources || []
  const sourceChip = srcs.includes('cdm') && srcs.includes('service_catalog')
    ? { label: 'both', cls: 'border-cyan/30 bg-cyan/10 text-cyan' }
    : srcs.includes('service_catalog')
      ? { label: 'catalog', cls: 'border-purple/30 bg-purple/10 text-purple-light' }
      : srcs.includes('cdm')
        ? { label: 'CDM', cls: 'border-border bg-surface-raised text-text-secondary' }
        : null
  return (
    <div className={clsx('card p-0 overflow-hidden', r.losing ? 'border-rose/20' : 'border-border')}>
      <button type="button" onClick={() => toggle(r.cve_id)}
              className="w-full flex items-center gap-2 p-3 text-left hover:bg-surface-raised">
        {open ? <ChevronDown size={14} className="text-text-muted" /> : <ChevronRight size={14} className="text-text-muted" />}
        <span className={clsx('text-[10px] font-mono px-1.5 py-0.5 rounded border w-12 text-center',
          r.race_score >= 60 ? 'bg-rose/10 text-rose-light border-rose/30'
            : r.race_score >= 30 ? 'bg-amber/10 text-amber-light border-amber/30'
            : 'bg-surface-raised text-text-muted border-border')}
          title="race score (blast × exposure × severity, controls-credited; normalised 0–100)">
          {Math.round(r.race_score)}
        </span>
        <Link to={`/app/cve/${encodeURIComponent(r.cve_id)}`} onClick={e => e.stopPropagation()}
              className="text-[11px] font-mono text-cyan hover:underline whitespace-nowrap">{r.cve_id}</Link>
        <SeverityBadge severity={sev} width="w-16" />
        {/* the race bar */}
        <div className="flex-1 min-w-[160px] max-w-[340px]"><RaceBar tte={r.predicted_tte_days} mttr={r.our_mttr_days} /></div>
        <div className="flex items-center gap-1 ml-auto flex-wrap justify-end">
          <span className="text-[9px] px-1.5 py-0.5 rounded border border-border bg-surface-raised text-text-secondary whitespace-nowrap"
                title="blast radius — affected assets × tier × toxic-chain × exposure">
            blast {Math.round(r.blast_radius)}
          </span>
          {r.ai_discovered && (
            <span className="text-[9px] px-1.5 py-0.5 rounded border border-amber/30 bg-amber/10 text-amber-light whitespace-nowrap inline-flex items-center gap-0.5">
              <Zap size={9} /> AI-found
            </span>
          )}
          {r.kev && (
            <span className="text-[9px] px-1.5 py-0.5 rounded border border-rose/30 bg-rose/10 text-rose-light whitespace-nowrap">KEV</span>
          )}
          {sourceChip && (
            <span className={clsx('text-[9px] px-1.5 py-0.5 rounded border whitespace-nowrap', sourceChip.cls)}
                  title="inventory source that surfaced this CVE">
              {sourceChip.label}
            </span>
          )}
          {r.version_confirmed === false && (
            <span className="text-[9px] px-1.5 py-0.5 rounded border border-amber/30 bg-amber/10 text-amber-light whitespace-nowrap"
                  title="declared version could not be confirmed against the CVE's affected range — surfaced as a possible hit">
              version-unverified
            </span>
          )}
          {r.internet_facing && (
            <span className="text-[9px] px-1.5 py-0.5 rounded border border-rose/20 text-rose-light/80 whitespace-nowrap inline-flex items-center gap-0.5">
              <Network size={9} /> exposed
            </span>
          )}
          {creditPct > 0 && (
            <span className="text-[9px] px-1.5 py-0.5 rounded border border-emerald/30 bg-emerald/10 text-emerald whitespace-nowrap inline-flex items-center gap-0.5"
                  title={topControls.join(' · ')}>
              <Shield size={9} /> −{creditPct}%
            </span>
          )}
        </div>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3 border-t border-border">
          {r.summary && <div className="text-[12px] text-text-secondary pt-2 leading-relaxed">{r.summary}</div>}

          {/* factor chips — why the window is short */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-text-muted mb-1">Why the exploit window is short</div>
            <div className="flex flex-wrap gap-1">
              {r.factors.map((f, i) => (
                <span key={i} className="text-[10px] px-1.5 py-0.5 rounded border border-border bg-surface-raised text-text-secondary"
                      title={f.detail}>
                  <span className="font-mono text-amber-light">{f.effect}</span> {f.factor.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
            <Stat label="Predicted TTE" value={`${r.predicted_tte_days}d`} tone="text-rose-light" />
            <Stat label="Our MTTR" value={`${r.our_mttr_days}d`} tone="text-cyan" />
            <Stat label="Exposure" value={`${r.exposure_days > 0 ? '+' : ''}${r.exposure_days}d`}
                  tone={r.losing ? 'text-rose-light' : 'text-emerald'} />
            <Stat label="EPSS" value={r.epss_score != null ? r.epss_score.toFixed(2) : '—'} />
            <Stat label="Affected assets" value={r.affected_asset_count} />
            <Stat label="Asset class" value={(r.dominant_asset_class || 'default').replace('asset_', '')} />
            <Stat label="CVSS" value={r.cvss_score ? r.cvss_score.toFixed(1) : '—'} />
            <Stat label="Tiers hit" value={(r.tiers_hit || []).join(', ') || '—'} />
          </div>

          {r.internet_facing && r.internet_facing_target_days != null && (
            <div className="text-[10px] text-amber-light/90 flex items-center gap-1">
              <Network size={11} /> Internet-facing — target patch window is {r.internet_facing_target_days}d,
              but the realistic default ({r.our_mttr_days}d) still applies. Mind the gap.
            </div>
          )}

          {(r.controls_credited || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-text-muted mb-1 flex items-center gap-1">
                <Shield size={11} className="text-emerald" /> Compensating controls credited
                <span className="font-mono text-emerald">−{creditPct}% loss</span>
              </div>
              <div className="flex flex-col gap-1">
                {(r.controls_credited || []).map((c, i) => (
                  <div key={i} className="flex items-center gap-2 text-[10px]">
                    <span className="font-mono text-emerald w-10 text-right">−{c.credit}</span>
                    <span className="text-text-secondary">{c.control}</span>
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-text-muted mt-1 italic">
                Controls reduce the cost of losing — not the predicted exploit time.
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 text-[10px]">
            {r.in_toxic_chain && (
              <span className="px-1.5 py-0.5 rounded border border-rose/30 text-rose-light inline-flex items-center gap-1">
                <ShieldAlert size={10} /> part of a toxic chain (×1.5 blast)
              </span>
            )}
            {r.ransomware && (
              <span className="px-1.5 py-0.5 rounded border border-rose/30 text-rose-light">ransomware use (×0.5 TTE)</span>
            )}
          </div>

          <RelatedItems entityType="race" entityKey={r.cve_id} />
        </div>
      )}
    </div>
  )
}

function Stat({ label, value, tone }: { label: string; value: React.ReactNode; tone?: string }) {
  return (
    <div className="rounded border border-border bg-surface-raised p-1.5">
      <div className="text-[9px] uppercase tracking-widest text-text-muted">{label}</div>
      <div className={clsx('text-[12px] font-semibold mt-0.5', tone || 'text-text-primary')}>{value}</div>
    </div>
  )
}
